import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/lib/utils";

type TabsAdditionalProps = { isAlt?: boolean };

const TabsAdditionalContext = React.createContext<TabsAdditionalProps>({});
const useTabsAdditional = () => React.useContext(TabsAdditionalContext);

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> &
    TabsAdditionalProps
>(({ isAlt, ...props }, ref) => (
  <TabsAdditionalContext.Provider value={{ isAlt }}>
    <TabsPrimitive.Root ref={ref} {...props} />
  </TabsAdditionalContext.Provider>
));

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const { isAlt } = useTabsAdditional();
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        isAlt
          ? "inline-flex items-center justify-center rounded-lg text-muted-foreground p-1 border border-border gap-1"
          : "inline-flex items-center justify-center text-muted-foreground font-normal gap-4",
        className,
      )}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
    border?: boolean;
    isAlt?: boolean;
  }
>(({ className, isAlt: _isAlt, border = true, ...props }, ref) => {
  const { isAlt: __isAlt } = useTabsAdditional();
  const isAlt = _isAlt ?? __isAlt;
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        isAlt
          ? "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-azure data-[state=active]:text-azure-foreground data-[state=active]:hover:bg-azure/80 hover:bg-muted hover:text-muted-foreground data-[state=active]:shadow"
          : "inline-flex items-center justify-center whitespace-nowrap disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-azure hover:text-foreground/80 ",
        !isAlt && border && "hover:border-b-2 hover:border-border",
        className,
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

export type TabsContentProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Content
> & {
  indent?: boolean;
};

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  TabsContentProps
>(({ ...props }, ref) => <TabsPrimitive.Content ref={ref} {...props} />);
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
